import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import {
  PaginatedFilterQuery,
  PaginatedResultDto_1OfCombinedWarehouseOrderItemDto,
} from "@lib/ShiOneClient";
import { CACHE_KEY_WAREHOUSE_ORDERS } from "@features/assets-feature/constants";
import ms from "ms";
import { getWarehouseOrders } from "@features/assets-feature/stores/warehouseStore";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import { ShiOnePermission } from "../../../../../../auth/authorizationTypes";

/**
 * Default query for consumers that don't implement their own pagination
 * (e.g. DemoShipping, ServiceRequestDetails, useAssetOrder, useWarehouseOrderMap).
 *
 * These components perform client-side lookups (.find() by ID or building a full
 * order map) and expect the complete dataset in a single request. Server-side
 * pagination was introduced for the Integration Center Orders grid because our
 * largest account will have 20k+ records. Other accounts are expected to have at
 * most a few thousand records per product management and AX team, so a 10,000 page size covers
 * them without needing full pagination support in every consumer.
 *
 * If average record counts grow significantly, these consumers should be migrated
 * to use dedicated single-order endpoints or implement their own pagination.
 */
const DEFAULT_WAREHOUSE_ORDERS_QUERY = new PaginatedFilterQuery({
  page: 1,
  pageSize: 10000,
  sortByColumn: "orderDate",
  isDescending: true,
});

export const useGetWarehouseOrders = (query: PaginatedFilterQuery = DEFAULT_WAREHOUSE_ORDERS_QUERY) => {
  const { hasPermission } = useClaims();
  const queryClient = useQueryClient();
  const hasWarehousePermission = hasPermission(ShiOnePermission.warehouseOrderRead);

  useEffect(() => {
    if (!hasWarehousePermission) {
      queryClient.removeQueries({ queryKey: CACHE_KEY_WAREHOUSE_ORDERS });
    }
  }, [hasWarehousePermission, queryClient]);

  return useQuery<PaginatedResultDto_1OfCombinedWarehouseOrderItemDto, Error>({
    queryKey: [...CACHE_KEY_WAREHOUSE_ORDERS, query],
    queryFn: () => getWarehouseOrders(query),
    staleTime: ms("1m"),
    keepPreviousData: true,
    enabled: hasWarehousePermission,
  });
};
